import React from 'react';

import { useAppSelector } from 'hooks/redux';
import type { RecordOf } from 'immutable';
import { DateTime } from 'luxon';

import { type accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import {
  AutoAdjustAmountEnum,
  mkScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import { billPresentmentSelectors } from 'data/billPresentment/slice';

import InfoIcon from '@mui/icons-material/Info';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import MenuButton from '@mui/icons-material/MoreVert';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { makeStyles } from 'tss-react/mui';

import ConnectedAvatar from 'components/ConnectedAvatar';
import { InitView, PendingView } from 'components/Dialogs/BillPresentment/New/HoldingScreen';
import Dump from 'components/Dump';
import QSwitch from 'components/QSwitch';

import type { STFormikProps } from '../types';

const useStyles = makeStyles()((theme) => ({
  connectBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    padding: 16,
    marginBottom: 24,
    gap: 8,
  },
  accountBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 16,
    backgroundColor: theme.palette.info.light,
    borderRadius: 12,
    gap: 8,
  },
  radioSection: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 32px 0 32px',
  },
  billField: {
    marginTop: 8,
    marginLeft: 12,
  },
  switch: {
    margin: 0,
    marginLeft: 16,
  },
  // Added classes for sx props in the selection
  billConnectCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: theme.palette.info.light,
    width: '100%',
  },
  billConnectCardLeft: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  billConnectCardAvatarBox: {
    margin: theme.spacing(2),
  },
  billConnectCardRight: {
    padding: theme.spacing(1),
  },
  menuUnlink: {
    color: theme.palette.error.main,
  },
  menuUnlinkIcon: {
    color: theme.palette.error.main,
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: 4,
    height: 20,
    width: 20,
  },
  billConnectInfoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const smallRadio = <Radio sx={{ padding: '6px', marginLeft: '3px' }} />;
const labelSlot = { typography: { variant: 'paragraphRegular' } } as any;

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

interface BillConnectTabProps {
  formikProps: STFormikProps;
  creditAccount?: RecordOf<accountsTypes.CreditAccountProps>;
  BVA: boolean;
  fiServ: boolean;
}

const tooltip = (
  <>
    <Typography variant='paragraphRegular' gutterBottom>
      Until your balance is finalized, Simplifi will calculate your amount due by adding your credit card transactions
      between your statement closing date and due date to provide a more accurate cash projection.
    </Typography>
    <Typography variant='paragraphRegular'>
      We&apos;ll estimate your statement closing date as three days before your due date if it&apos;s not provided by
      your financial institution.
    </Typography>
  </>
);

function BillConnectTab({ formikProps, creditAccount, BVA, fiServ }: BillConnectTabProps) {
  const { classes } = useStyles();

  const presentmentAccountId = formikProps.values?.presentmentAccountId || '';

  const billerAccount = useAppSelector((state) =>
    billPresentmentSelectors.selectBillerAccountById(state, presentmentAccountId),
  );
  const biller = useAppSelector(
    (state) => billerAccount && billPresentmentSelectors.selectBillerById(state, billerAccount.billerId),
  );

  const { autoAdjustAmount, autoAdjustDueOn, transaction } = formikProps.values || {};

  const model = mkScheduledTransaction({
    ...(formikProps.values || {}),
    dueOn: formikProps.values?.dueOn?.toISODate(),
  } as any);

  const allDisabled = Boolean(
    (!autoAdjustAmount || autoAdjustAmount === AutoAdjustAmountEnum.none) && !autoAdjustDueOn,
  );
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const dueDate =
    creditAccount && DateTime.fromISO(creditAccount.statementDueAt || creditAccount.statementDueDate || '');
  const dueDay = dueDate?.isValid ? `${ordinal(dueDate.day)} day of the month` : ' - ';
  const closeDate = creditAccount && DateTime.fromISO(creditAccount.statementCloseAt || '');
  const closeDay = closeDate?.isValid ? `${ordinal(closeDate.day)} day of the month` : ' - ';

  if (!BVA && !billerAccount) {
    return <InitView />;
  }
  if (!BVA && billerAccount && model.billerEnrollmentStatus === 'PENDING') {
    return <PendingView />;
  }

  return (
    <>
      <div className={classes.connectBox}>
        <Stack flexDirection='row'>
          <Typography variant='bodyMedium'>Bill Connect</Typography>

          {BVA && (
            <QSwitch
              id='disable-bill-connect-switch'
              checked={!allDisabled}
              onChange={() => {
                if (allDisabled) {
                  formikProps.setFieldValue('autoAdjustAmount', AutoAdjustAmountEnum.statement);
                } else {
                  formikProps.setFieldValue('autoAdjustAmount', AutoAdjustAmountEnum.none);
                  formikProps.setFieldValue('autoAdjustDueOn', false);
                }
              }}
              className={classes.switch}
            />
          )}
        </Stack>
        {BVA && (
          <Typography variant='bodyRegular'>
            Automatically update the amount and due date of your reminders by connecting this series to the matching
            connected account in Simplifi.
          </Typography>
        )}

        {fiServ && (
          <Typography variant='bodyRegular'>
            Automatically updates the amount and due date of your Reminders once a new bill arrives.
          </Typography>
        )}
        {BVA && (
          <div className={classes.accountBox} style={{ opacity: allDisabled ? 0.4 : 1 }}>
            <Stack flexDirection='row' width='100%' alignItems='center' mb={2}>
              <Dump obj={creditAccount} />

              <ConnectedAvatar txn={transaction as TransactionProps} />

              <Stack ml={2}>
                <Typography variant='captionRegular' color='textSecondary'>
                  Connected account
                </Typography>
                <Typography variant='bodyMedium'>
                  {`${creditAccount?.name || ' - No credit account found - '} ${accountsUtils?.formatFinalFour(creditAccount)}`}
                </Typography>
              </Stack>
            </Stack>

            <div className={classes.billConnectInfoContainer}>
              <Typography
                variant='paragraphRegular'
                color='textSecondary'
              >{`We'll calculate your balance using transactions between these dates.`}</Typography>
              <Tooltip arrow placement='right' title={tooltip}>
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            </div>

            <Stack flexDirection='row' width='100%'>
              <TextField
                label='Statement closing date'
                value={closeDay}
                variant='standard'
                fullWidth
                className={classes.billField}
                slotProps={{
                  input: { readOnly: true, disableUnderline: true },
                }}
              />

              <TextField
                label='Due date'
                value={dueDay}
                variant='standard'
                fullWidth
                className={classes.billField}
                slotProps={{
                  input: { readOnly: true, disableUnderline: true },
                }}
              />
            </Stack>
          </div>
        )}

        {fiServ && billerAccount && (
          <Box className={classes.billConnectCard}>
            <Dump obj={biller} />
            <Box className={classes.billConnectCardLeft}>
              <Box className={classes.billConnectCardAvatarBox}>
                <ConnectedAvatar txn={transaction as TransactionProps} model={model} />
              </Box>
              <Box>
                <Typography variant='paragraphMedium' color='textSecondary'>
                  {biller?.name || 'TEST ACCOUNT'}
                </Typography>
                <Typography variant='captionRegular'>{biller?.logoKey || billerAccount?.id}</Typography>
              </Box>
            </Box>
            <Box className={classes.billConnectCardRight}>
              <ButtonBase
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
                focusRipple
                style={{ borderRadius: 8 }}
              >
                <Dump obj={billerAccount} />
                <MenuButton />
              </ButtonBase>
              {menuAnchor && (
                <Menu
                  id='bill-connect-menu'
                  anchorEl={menuAnchor}
                  keepMounted
                  open
                  onClose={() => setMenuAnchor(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    id='menu-unlink'
                    className={classes.menuUnlink}
                    onClick={() => {
                      'do nothing';
                    }}
                  >
                    <ListItemIcon className={classes.menuUnlinkIcon}>
                      <UnlinkIcon />
                    </ListItemIcon>
                    Disconnect biller
                  </MenuItem>
                </Menu>
              )}
            </Box>
          </Box>
        )}
      </div>

      <Typography variant='paragraphRegular' color='textSecondary'>
        When your statement period ends, update reminder to:
      </Typography>

      <div className={classes.radioSection} style={{ opacity: allDisabled ? 0.4 : 1 }}>
        <FormControl fullWidth>
          <Typography variant='paragraphMedium' id='bill-amount-label'>
            Amount
          </Typography>
          <RadioGroup
            aria-labelledby='bill-amount-label'
            defaultValue={AutoAdjustAmountEnum.statement}
            name='bill-amount-group'
            value={autoAdjustAmount}
            onChange={(_, value) => {
              formikProps.setFieldValue('autoAdjustAmount', value);
            }}
          >
            <FormControlLabel
              value={AutoAdjustAmountEnum.statement}
              label='Statement balance'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.minimum}
              label='Minimum due'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.total}
              label='Total Balance'
              control={smallRadio}
              slotProps={labelSlot}
            />
            <FormControlLabel
              value={AutoAdjustAmountEnum.none}
              label="Don't update (use series amount)"
              control={smallRadio}
              slotProps={labelSlot}
            />
          </RadioGroup>
        </FormControl>

        <FormControl fullWidth>
          <Typography variant='paragraphMedium' id='bill-date-label'>
            Date
          </Typography>
          <RadioGroup
            aria-labelledby='bill-date-label'
            defaultValue='false'
            name='bill-date-group'
            value={String(autoAdjustDueOn)}
            onChange={(_, value) => {
              formikProps.setFieldValue('autoAdjustDueOn', value === 'true' ? true : false);
            }}
          >
            <FormControlLabel value='true' label='Due Date' control={smallRadio} slotProps={labelSlot} />
            <FormControlLabel
              value='false'
              label="Don't update (use series date)"
              control={smallRadio}
              slotProps={labelSlot}
            />
          </RadioGroup>
        </FormControl>
      </div>
    </>
  );
}

BillConnectTab.displayName = 'BillConnectTab';

export default BillConnectTab;
